import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  CompanyGroupService,
  CompanyGroup,
  GroupMember,
  InviteLink,
} from '../../services/company-group.service';
import { CompanyService, Company } from '../../services/company.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { environment } from '../../../environments/environment';
import { companyInitials } from '../../components/layout/navbar/company-initials';
import { MemberStatus, memberSince, memberStatus } from '../../utils/member-status.utils';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideCircleCheck,
  lucideGlobe,
  lucideLandmark,
  lucideLink,
  lucideMail,
  lucideMapPin,
  lucidePhone,
  lucideTriangleAlert,
  lucideUsers,
  lucideX,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmAvatarImports } from '@spartan-ng/helm/avatar';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';

@Component({
  selector: 'app-group-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmAvatarImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmEmptyImports,
    HlmInputImports,
    HlmItemImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTableImports,
    HlmTooltipImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideArrowRight,
      lucideCircleCheck,
      lucideGlobe,
      lucideLandmark,
      lucideLink,
      lucideMail,
      lucideMapPin,
      lucidePhone,
      lucideTriangleAlert,
      lucideUsers,
      lucideX,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './group-management.component.html',
})
export class GroupManagementComponent implements OnInit {
  groups: CompanyGroup[] = [];
  selectedGroup: CompanyGroup | null = null;
  currentCompanyId: string | null = null;

  isLoading = false;
  error: string | null = null;
  successMessage: string | null = null;
  copiedToken = false;
  approvingMemberId: string | null = null;
  rejectingMemberId: string | null = null;

  inviteEmail = '';
  inviting = false;
  creatingLink = false;
  freshLink: InviteLink | null = null;
  private freshLinkTimer: ReturnType<typeof setTimeout> | null = null;

  currentCompany: Company | null = null;

  constructor(
    private groupService: CompanyGroupService,
    private companyService: CompanyService,
    private tokenStorage: TokenStorageService,
  ) {
    this.currentCompanyId = this.tokenStorage.getPublicCompanyId();
  }

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    // company$ never completes — without a teardown every visit to this route
    // left a permanent subscriber behind.
    this.companyService.company$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((company) => {
      this.currentCompany = company;
    });
    this.loadGroups();
  }

  loadGroups(): void {
    this.isLoading = true;
    this.error = null;
    this.groupService.listGroups().subscribe({
      next: (groups) => {
        this.groups = groups;
        // Always show the first (and only) group directly
        this.selectedGroup = groups[0] ?? null;
        this.isLoading = false;
      },
      error: (err) => {
        this.error = err.error?.detail || 'Das Netzwerk konnte nicht geladen werden.';
        this.isLoading = false;
      },
    });
  }

  selectGroup(group: CompanyGroup): void {
    this.selectedGroup = group;
    this.error = null;
    this.successMessage = null;
  }

  isCentral(group: CompanyGroup): boolean {
    return group.central_company_id === this.currentCompanyId;
  }

  initials(name: string | null): string {
    return companyInitials(name ?? undefined);
  }

  /** Logo of a company; the images endpoint already includes the uploads/ segment. */
  imageUrl(path: string | null): string | null {
    if (!path) return null;
    return `${environment.apiUrl}/images/${path.startsWith('uploads/') ? path.slice(8) : path}`;
  }

  websiteHref(website: string): string {
    return /^https?:\/\//i.test(website) ? website : `https://${website}`;
  }

  websiteLabel(website: string): string {
    return website.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }

  myStatus(me: Company): MemberStatus {
    return memberStatus(me);
  }

  myMembership(group: CompanyGroup): GroupMember | null {
    return group.members.find((m) => m.company_id === this.currentCompanyId) ?? null;
  }

  memberSince(joinedAt: string): string {
    return memberSince(joinedAt);
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDateShort(dateStr: string | null): string {
    if (!dateStr) return '–';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  /** Multi-link helpers */
  isLinkExpired(link: InviteLink): boolean {
    return new Date(link.expires_at) <= new Date();
  }

  /** Members are paid by bank transfer: the IBAN they entered in onboarding. */
  hasPayoutAccount(member: GroupMember): boolean {
    return !!member.has_bank_account;
  }

  /** A member can be approved once it finished onboarding, IBAN included. */
  canApprove(member: GroupMember): boolean {
    return !!member.onboarding_complete;
  }

  buildLinkUrl(token: string): string {
    return this.groupService.buildInviteUrl(token);
  }

  createInviteLink(group: CompanyGroup): void {
    this.creatingLink = true;
    this.error = null;
    this.groupService.createInviteLink(group.public_id).subscribe({
      next: (link) => {
        this.creatingLink = false;
        this.freshLink = link;
        navigator.clipboard.writeText(this.buildLinkUrl(link.token)).catch(() => {});
        if (this.freshLinkTimer) clearTimeout(this.freshLinkTimer);
        this.freshLinkTimer = setTimeout(() => (this.freshLink = null), 60_000);
      },
      error: (err) => {
        this.error = err.error?.detail || 'Fehler beim Erstellen des Links.';
        this.creatingLink = false;
      },
    });
  }

  dismissFreshLink(): void {
    this.freshLink = null;
    if (this.freshLinkTimer) clearTimeout(this.freshLinkTimer);
  }

  getMemberCount(group: CompanyGroup): number {
    return group.members.filter((m) => m.status === 'active').length;
  }

  getPendingMembers(group: CompanyGroup) {
    return group.members.filter((m) => m.status === 'pending');
  }

  getActiveMembers(group: CompanyGroup) {
    return group.members.filter((m) => m.status === 'active');
  }

  inviteMember(group: CompanyGroup): void {
    const email = this.inviteEmail.trim();
    if (!email) return;
    this.inviting = true;
    this.error = null;
    this.groupService.inviteMemberByEmail(group.public_id, email).subscribe({
      next: () => {
        this.successMessage = `Einladung an ${email} wurde gesendet.`;
        this.inviteEmail = '';
        this.inviting = false;
        this.loadGroups();
      },
      error: (err) => {
        this.error = err.error?.detail || 'Fehler beim Einladen.';
        this.inviting = false;
      },
    });
  }

  approveMember(group: CompanyGroup, memberId: string, memberName: string | null): void {
    this.approvingMemberId = memberId;
    this.error = null;
    this.groupService.approveMember(group.public_id, memberId).subscribe({
      next: () => {
        this.successMessage = `${memberName || memberId} wurde bestätigt.`;
        this.approvingMemberId = null;
        this.loadGroups();
      },
      error: (err) => {
        this.error = err.error?.detail || 'Fehler beim Bestätigen des Mitglieds.';
        this.approvingMemberId = null;
      },
    });
  }

  rejectMember(group: CompanyGroup, memberId: string, memberName: string | null): void {
    if (!confirm(`${memberName || memberId} wirklich ablehnen?`)) return;
    this.rejectingMemberId = memberId;
    this.error = null;
    this.groupService.rejectMember(group.public_id, memberId).subscribe({
      next: () => {
        this.successMessage = `${memberName || memberId} wurde abgelehnt.`;
        this.rejectingMemberId = null;
        this.loadGroups();
      },
      error: (err) => {
        this.error = err.error?.detail || 'Fehler beim Ablehnen des Mitglieds.';
        this.rejectingMemberId = null;
      },
    });
  }
}
